import { NODE_DATA_KEY_GROUPS } from './nodeConfigKeys';
import {
  DEFAULT_CONFIG_BY_TOOL_TYPE,
  type AppEdge,
  type AppNode,
  type BackendNodeKind,
  type ExecutionScope,
  type ExecutionScopeDto,
  type FailedCard,
  type FailedCardDto,
  type GraphEdgeDto,
  type GraphNodeDto,
  type Job,
  type JobAsset,
  type JobAssetDto,
  type JobCancelResult,
  type JobCancelResultDto,
  type JobContinueResult,
  type JobContinueResultDto,
  type JobDetail,
  type JobDetailDto,
  type JobDto,
  type JobList,
  type JobListDto,
  type JobSummary,
  type JobSummaryDto,
  type JobGraphSnapshot,
  type JobGraphSnapshotDto,
  type JobSubmitResult,
  type JobSubmitResultDto,
  type NodeConfig,
  type NodeKind,
  type Step,
  type StepDto,
  type StepErrorInfo,
  type StepErrorInfoDto,
  type SdkPreviewInput,
  type SdkPreviewInputDto,
  type SdkRequestPreview,
  type SdkRequestPreviewDto,
  type StepProgressHint,
  type StepProgressHintDto,
  type ToolType,
} from './job.types';
import { resolveToolTypeFromNodeData } from './job.guards';

/** UPPERCASE backend NodeKind ↔ lowercase frontend NodeKind 雙向轉。 */
export function appNodeKindToBackend(kind: NodeKind): BackendNodeKind {
  return kind.toUpperCase() as BackendNodeKind;
}

export function backendNodeKindToApp(kind: BackendNodeKind): NodeKind {
  return kind.toLowerCase() as NodeKind;
}

/**
 * 從 `AppNode` 推 ToolType。Stage B worker 嚴格 enum — 送 lowercase 或非標準字串
 * → step FAILED + INVALID_INPUT + retryable=False。
 *
 * 來源優先序由 `resolveToolTypeFromNodeData` 統一處理(結構化 `toolType` → meta-parse);
 * 推不出時 submit 路徑補 `'IMAGE'` safe default(credit 中等)。非 TOOL node 回 null。
 */
function inferToolType(node: AppNode): ToolType | null {
  if (node.type !== 'tool') return null;
  const data = node.data as { toolType?: string; meta?: string };
  return resolveToolTypeFromNodeData(data) ?? 'IMAGE';
}

/**
 * 送 BE 前要從 TOOL `config` 剝掉的 key(P3 — 對齊 docs/44 §2 `extra="forbid"`)。
 * -----------------------------------------------------------------------------
 * 來自 strip-key 分組 SoT `@/types/nodeConfigKeys`(`NODE_DATA_KEY_GROUPS`)。submit
 * (`/jobs`)路徑取 `feMetadata ∪ runtimeOnly`;`nodeAdapter` 的 persist(`/nodes`+`/graph`)
 * 路徑另取全三組。兩條路徑都送 typed `config`,BE worker 對 TOOL config 嚴格 white-list
 * (`label` / `meta` / `assetRef` / `status` / runtime-only 進 config → 422
 * `NODE_CONFIG_INVALID`)。改用共用 SoT 後不再需要兩處手動同步;新增渲染 / FE-metadata
 * 欄位只補在 SoT 一處。
 *
 * 註:runtimeOnly 含 `locked`(ADR-026 server-owned 唯讀旗標)與 `jobPhase`(D5;產生者
 * 與消費者皆已終刪,只剩 strip key 防存量殘值)—— 兩者都是 top-level node.data 旗標、
 * 永不進 `config`,納入剝除 set 為行為等價防呆(submit 輸出 config 不變)。
 *
 * `tool_type` **不**剝 — docs/44 §2 rule 5 明確允許 `config.tool_type`(若帶須等於節點
 * tool_type;下方 `finalConfig` 保證對齊),作為 BE NodeConfig discriminated union 的判別子。
 */
const SUBMIT_CONFIG_STRIP_KEYS = new Set<string>([
  ...NODE_DATA_KEY_GROUPS.feMetadata,
  ...NODE_DATA_KEY_GROUPS.runtimeOnly,
  ...NODE_DATA_KEY_GROUPS.retired,
]);

/**
 * 剝掉 FE-metadata / runtime-only key,只留 catalog 認得的 typed config + `tool_type`。
 * `undefined` 值也丟掉(送 `null` 才是「主動不設」,`undefined` 是「沒這欄」)。
 *
 * `rawConfig` 接 `Record<string, unknown>`(user 設定)或 `NodeConfig`(default fallback —
 * 無 index signature 的 discriminated union;`Object.entries` 容忍任意物件,免 `as unknown` cast)。
 */
function buildSubmitConfig(
  rawConfig: Record<string, unknown> | NodeConfig,
  toolType: ToolType,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(rawConfig)) {
    if (SUBMIT_CONFIG_STRIP_KEYS.has(k)) continue;
    if (v === undefined) continue;
    out[k] = v;
  }
  // 保證 discriminator 對齊 outer tool_type(user 改 toolType 但忘了同步 config 的情況)。
  out.tool_type = toolType;
  return out;
}

/**
 * `AppNode` → backend wire format `GraphNodeDto`(submit `/jobs` 用)。
 *
 * - TOOL node — 送 `tool_type`(嚴格 enum)+ **乾淨** `config`:只留 catalog 認得的 typed
 *   key + `tool_type` discriminator(剝掉 FE-metadata / runtime-only,見
 *   `SUBMIT_CONFIG_STRIP_KEYS`),否則 BE worker `extra="forbid"` 會 422 `NODE_CONFIG_INVALID`。
 * - ROOT / OUTPUT node — 不送 `tool_type` / `config`(BE 不解讀 TOOL config;OUTPUT 的
 *   `asset_id` 是 `read_only` server-projected,FE 不夾帶 → 不送 config 自然不會帶到)。
 * - `position` 保留;`data` 仍 pass-through 給 Job echo 回來顯示用(label / meta;BE outer
 *   `extra="allow"`),但 `data` 不參與 config 驗證。
 */
export function appNodeToGraphNodeDto(node: AppNode): GraphNodeDto {
  const nodeKind = appNodeKindToBackend(node.type);
  if (nodeKind !== 'TOOL') {
    // ROOT / OUTPUT:不送 config / tool_type。data pass-through(echo 回顯示),BE 不驗。
    // OUTPUT.asset_id 是 read_only top-level projection,不在 data.config 內 → 天然不夾帶。
    return {
      node_id: node.id,
      node_kind: nodeKind,
      position: node.position,
      data: node.data,
    };
  }
  const toolType = inferToolType(node) ?? 'IMAGE';
  const userConfig = (node.data as { config?: Record<string, unknown> }).config;
  // userConfig 是 user 設定(Record);fallback 是 NodeConfig default(無 index signature 的
  // discriminated union)。buildSubmitConfig 接 union 後內部走 Object.entries,免 `as unknown` 橋接。
  const rawConfig: Record<string, unknown> | NodeConfig =
    userConfig ?? DEFAULT_CONFIG_BY_TOOL_TYPE[toolType];
  // 剝 FE-metadata / runtime-only + 對齊 tool_type discriminator(只送 catalog 認得的 key)。
  const finalConfig = buildSubmitConfig(rawConfig, toolType);
  return {
    node_id: node.id,
    node_kind: 'TOOL',
    tool_type: toolType,
    config: finalConfig,
    position: node.position,
    data: node.data,
  };
}

export function appEdgeToGraphEdgeDto(edge: AppEdge): GraphEdgeDto {
  return {
    edge_id: edge.id,
    source_node_id: edge.source,
    target_node_id: edge.target,
    data: edge.data ?? {},
  };
}

/** Backend echo 回來的 graph node DTO 還原成前端 `AppNode`。 */
export function graphNodeDtoToAppNode(dto: GraphNodeDto): AppNode {
  const passthrough = dto as { position?: { x: number; y: number }; data?: AppNode['data'] };
  return {
    id: dto.node_id,
    type: backendNodeKindToApp(dto.node_kind),
    position: passthrough.position ?? { x: 0, y: 0 },
    data:
      passthrough.data ??
      ({ kind: backendNodeKindToApp(dto.node_kind), label: dto.node_id } as AppNode['data']),
  };
}

export function graphEdgeDtoToAppEdge(dto: GraphEdgeDto): AppEdge {
  return {
    id: dto.edge_id,
    source: dto.source_node_id,
    target: dto.target_node_id,
    data:
      typeof dto.data === 'object' && dto.data !== null
        ? (dto.data as Record<string, unknown>)
        : {},
  };
}

export function jobGraphSnapshotToDto(g: JobGraphSnapshot): JobGraphSnapshotDto {
  return {
    nodes: g.nodes.map(appNodeToGraphNodeDto),
    edges: g.edges.map(appEdgeToGraphEdgeDto),
    schema_version: g.schemaVersion,
  };
}

export function jobGraphSnapshotFromDto(dto: JobGraphSnapshotDto): JobGraphSnapshot {
  return {
    nodes: dto.nodes.map(graphNodeDtoToAppNode),
    edges: dto.edges.map(graphEdgeDtoToAppEdge),
    schemaVersion: dto.schema_version,
  };
}

export function executionScopeToDto(scope: ExecutionScope): ExecutionScopeDto {
  switch (scope.mode) {
    case 'all':
      return { mode: 'all' };
    case 'from_node':
      return { mode: 'from_node', start_node_ids: scope.startNodeIds };
    case 'single_node':
      return { mode: 'single_node', node_id: scope.nodeId };
    case 'subset':
      // R137(docs/76 §4.2)— 直列綠卡 node_ids(舊 start/end 形隨 JobScopeDialog 刪)。
      return { mode: 'subset', node_ids: scope.nodeIds };
  }
}

export function executionScopeFromDto(dto: ExecutionScopeDto): ExecutionScope {
  switch (dto.mode) {
    case 'all':
      return { mode: 'all' };
    case 'from_node':
      return { mode: 'from_node', startNodeIds: dto.start_node_ids ?? [] };
    case 'single_node':
      return { mode: 'single_node', nodeId: dto.node_id ?? '' };
    case 'subset':
      // 容舊:S 系列前的存量 job detail 可能仍是 start/end 形 → 併成 nodeIds 近似讀回。
      return {
        mode: 'subset',
        nodeIds: dto.node_ids ?? [...(dto.start_node_ids ?? []), ...(dto.end_node_ids ?? [])],
      };
  }
}

function mapStepErrorInfoDto(dto: StepErrorInfoDto): StepErrorInfo {
  return {
    code: dto.code,
    message: dto.message,
    retryable: dto.retryable,
    // R277(docs/98 §2.3)— 舊 job 三欄缺席 → null(顯示端退回 errorClass 對照)。
    failureCode: dto.failure_code ?? null,
    userMessage: dto.user_message ?? null,
    userAction: dto.user_action ?? null,
  };
}

/** R277(docs/98 §2.2)— `failed_cards[]` 單筆;缺欄補 null(mapper 邊界慣例)。 */
export function mapFailedCardDto(dto: FailedCardDto): FailedCard {
  return {
    cardNid: dto.card_nid,
    nodeId: dto.node_id,
    stepId: dto.step_id,
    toolType: dto.tool_type ?? null,
    errorCode: dto.error_code ?? null,
    failureCode: dto.failure_code ?? null,
    message: dto.message ?? null,
    userMessage: dto.user_message ?? null,
    userAction: dto.user_action ?? null,
    userRetryable: dto.user_retryable ?? null,
    // 缺欄(舊 job)→ 空陣列;元素形狀非 object 一律丟棄(自由 dict,不猜)
    partialAssets: Array.isArray(dto.partial_assets)
      ? dto.partial_assets.filter((a): a is Record<string, string> => !!a && typeof a === 'object')
      : [],
  };
}

function mapStepProgressHintDto(dto: StepProgressHintDto): StepProgressHint {
  return {
    phase: dto.phase,
    startedAt: dto.started_at,
    estimatedTotalSeconds: dto.estimated_total_seconds,
  };
}

/**
 * `sdk_request_preview` normalize(docs/76 §5.6)。BE 端是自由 dict(`dict[str, Any]`)
 * 且欄位值域**開放**(reason 之後還會加)→ 全程防禦式:整包非 object 回 null、
 * inputs 非 array 當空、單筆非 object 丟棄、欄位型別不對補 null。**不驗值域** ——
 * 憑證的意義是「BE 說了什麼就顯什麼」,認不出的 reason 由顯示端落通用文案。
 */
function mapSdkRequestPreviewDto(raw: unknown): SdkRequestPreview | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const dto = raw as SdkRequestPreviewDto;
  const mapInputs = (list: unknown): SdkPreviewInput[] => {
    if (!Array.isArray(list)) return [];
    const out: SdkPreviewInput[] = [];
    for (const item of list) {
      if (item == null || typeof item !== 'object' || Array.isArray(item)) continue;
      const i = item as SdkPreviewInputDto;
      out.push({
        kind: typeof i.kind === 'string' && i.kind ? i.kind : null,
        sourceNodeId:
          typeof i.source_node_id === 'string' && i.source_node_id ? i.source_node_id : null,
        asText: i.as_text === true,
        reason: typeof i.reason === 'string' && i.reason ? i.reason : null,
      });
    }
    return out;
  };
  const consumed = mapInputs(dto.consumed_inputs);
  const ignored = mapInputs(dto.ignored_inputs);
  const model = typeof dto.model === 'string' && dto.model ? dto.model : null;
  const brandKitRef =
    typeof dto.brand_kit_ref === 'string' && dto.brand_kit_ref ? dto.brand_kit_ref : null;
  // 四個顯示面全空 = 沒有可顯示的憑證 → null(顯示端整段不 render,不畫空殼)。
  if (consumed.length === 0 && ignored.length === 0 && !model && !brandKitRef) return null;
  return { model, consumedInputs: consumed, ignoredInputs: ignored, brandKitRef };
}

export function mapStepDto(dto: StepDto): Step {
  return {
    stepId: dto.step_id,
    nodeId: dto.node_id,
    nodeKind: dto.node_kind,
    toolType: dto.tool_type,
    status: dto.status,
    dependsOn: dto.depends_on,
    reservedCredits: dto.reserved_credits,
    actualCredits: dto.actual_credits,
    attemptCount: dto.attempt_count,
    outputAssetId: dto.output_asset_id,
    errorInfo: dto.error_info ? mapStepErrorInfoDto(dto.error_info) : null,
    enqueuedDatetime: dto.enqueued_datetime,
    startedDatetime: dto.started_datetime,
    completedDatetime: dto.completed_datetime,
    updatedDatetime: dto.updated_datetime,
    operationId: dto.operation_id ?? null,
    errorClass: dto.error_class ?? null,
    appliedPrompt: dto.applied_prompt ?? null,
    progressHint: dto.progress_hint ? mapStepProgressHintDto(dto.progress_hint) : null,
    nextPollDatetime: dto.next_poll_datetime ?? null,
    sdkRequestPreview: mapSdkRequestPreviewDto(dto.sdk_request_preview),
    // docs/75 §6 — 段落血緣(`card_nid` 把隱藏 step 收斂回畫布卡);缺值 → 空 dict。
    lineage: dto.lineage ?? {},
  };
}

export function mapJobAssetDto(dto: JobAssetDto): JobAsset {
  return {
    assetId: dto.asset_id,
    stepId: dto.step_id,
    nodeId: dto.node_id,
    assetKind: dto.asset_kind,
    gcsUri: dto.gcs_uri,
    metadata: dto.metadata,
    // R134(docs/75 §6)— A/B 段落血緣;缺值 normalize 成空 dict(mapper 邊界慣例)。
    lineage: dto.lineage ?? {},
    version: dto.version,
    createdDatetime: dto.created_datetime,
  };
}

/** R274.1 — 生成紀錄清單一列(BE docs/97 §1)。缺欄 normalize 同 `mapJobDto` 慣例。 */
export function mapJobSummaryDto(dto: JobSummaryDto): JobSummary {
  return {
    jobId: dto.job_id,
    // R285 — wire 從 docs/97 §1 起就帶 scope,R274.1 漏接(成品計數因此只能攤平、把
    // 隱藏中繼段算成成品)。舊 BE 缺欄 → null(消費端 fallback 攤平)。
    executionScope: dto.execution_scope ? executionScopeFromDto(dto.execution_scope) : null,
    overallStatus: dto.overall_status,
    pausedReason: dto.paused_reason ?? null,
    abortReason: dto.abort_reason ?? null,
    failedStepIds: dto.failed_step_ids ?? [],
    failedCards: (dto.failed_cards ?? []).map(mapFailedCardDto),
    totalSteps: dto.total_steps,
    completedSteps: dto.completed_steps,
    outputNodeAssets: dto.output_node_assets ?? {},
    submittedDatetime: dto.submitted_datetime ?? null,
    completedDatetime: dto.completed_datetime ?? null,
  };
}

export function mapJobListDto(dto: JobListDto): JobList {
  return {
    jobs: (dto.jobs ?? []).map(mapJobSummaryDto),
    nextCursor: dto.next_cursor ?? null,
  };
}

export function mapJobDto(dto: JobDto): Job {
  // R41.0 defensive ?? — BE response 偶爾缺 array / record field(例如 Job 剛 create
  // 還沒 failed 任何 step,BE 可能省 failed_step_ids);component 端 `.length` /
  // `.includes` access 在 undefined 上 crash。mapper 邊界一次 normalize 比每個 caller
  // 散落 `??` 乾淨,也符合 ADR-002 + SSOT「FE 拿到的 shape 始終完整」契約。
  return {
    jobId: dto.job_id,
    projectId: dto.project_id,
    executionScope: executionScopeFromDto(dto.execution_scope),
    graphSnapshot: jobGraphSnapshotFromDto(dto.graph_snapshot),
    brandKitId: dto.brand_kit_id,
    estimatedTotalCredits: dto.estimated_total_credits,
    actualCredits: dto.actual_credits,
    overallStatus: dto.overall_status,
    pausedReason: dto.paused_reason,
    // R285(docs/103 §3.5)— CANCELLED 成因;三路(REST detail / list / Firestore doc)
    // 都走這支或 mapJobSummaryDto,缺欄(舊 doc)→ null = 使用者按的取消語意。
    abortReason: dto.abort_reason ?? null,
    failedStepIds: dto.failed_step_ids ?? [],
    failedCards: (dto.failed_cards ?? []).map(mapFailedCardDto),
    currentStepIds: dto.current_step_ids ?? [],
    totalSteps: dto.total_steps,
    completedSteps: dto.completed_steps,
    outputNodeAssets: dto.output_node_assets ?? {},
    // R111(docs/67 §3.2)— 重用節點清單(步驟列「已完成(重用)」標示);未帶 → []。
    reusedNodeIds: dto.reused_node_ids ?? [],
    version: dto.version,
    continueCount: dto.continue_count,
    submittedDatetime: dto.submitted_datetime,
    startedDatetime: dto.started_datetime,
    completedDatetime: dto.completed_datetime,
    cancelledDatetime: dto.cancelled_datetime,
    updatedDatetime: dto.updated_datetime,
  };
}

export function mapJobDetailDto(dto: JobDetailDto): JobDetail {
  // R41.0 defensive ?? — 同上 mapJobDto。BE 在某些 transient state(job 剛建立 / firestore
  // listener 提早 fire)可能回 partial JobDetail(缺 steps / assets array)。
  const detail: JobDetail = {
    job: mapJobDto(dto.job),
    steps: (dto.steps ?? []).map(mapStepDto),
    assets: (dto.assets ?? []).map(mapJobAssetDto),
  };
  if (dto.firestore_paths) {
    detail.firestorePaths = {
      project: dto.firestore_paths.project,
      job: dto.firestore_paths.job,
      stepsCollection: dto.firestore_paths.steps_collection,
      assetsCollection: dto.firestore_paths.assets_collection,
    };
  }
  return detail;
}

export function mapJobSubmitResultDto(dto: JobSubmitResultDto): JobSubmitResult {
  return {
    jobId: dto.job_id,
    estimatedCredits: dto.estimated_credits,
    status: dto.status,
    totalSteps: dto.total_steps,
    // R111(docs/67 §3.3)— 重用節點清單;後端未帶(過渡期舊版 / 無重用)→ []。
    reusedNodeIds: dto.reused_node_ids ?? [],
  };
}


export function mapJobContinueResultDto(dto: JobContinueResultDto): JobContinueResult {
  return {
    jobId: dto.job_id,
    resumedStepIds: dto.resumed_step_ids,
    estimatedAdditionalCredits: dto.estimated_additional_credits,
  };
}

export function mapJobCancelResultDto(dto: JobCancelResultDto): JobCancelResult {
  return {
    jobId: dto.job_id,
    status: dto.status,
    refundedCredits: dto.refunded_credits,
  };
}

// Asset signed-URL / per-project listing 的 mapper 已搬到 `types/assets.mappers.ts`
// (assetsService 第一公民 service,型別不再寄居 jobs);`mapJobAssetDto` 仍留本檔,
// `assets.mappers.ts` import 它組裝 `AssetWithJobContext`(耦合靠 import 保留)。
